import { Chart as ChartJS } from 'chart.js';
import html2canvas from 'html2canvas';

export type MsrChartImageFormat = 'png' | 'jpeg';

function dataUrlToBlob(dataUrl: string): Blob {
  const parts = dataUrl.split(',');
  const base64 = parts[1] ?? '';
  const mime = parts[0]?.match(/:(.*?);/)?.[1] ?? 'image/png';
  const bytes = atob(base64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load chart image'));
    img.src = dataUrl;
  });
}

function chartFilename(chartTitle: string, format: MsrChartImageFormat): string {
  const safe = chartTitle.replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_|_$/g, '') || 'chart';
  const ext = format === 'png' ? 'png' : 'jpg';
  return `${safe}.${ext}`;
}

async function exportFromChartJsCanvas(
  canvas: HTMLCanvasElement,
  format: MsrChartImageFormat
): Promise<Blob | null> {
  const chart = ChartJS.getChart(canvas);
  if (!chart) return null;

  chart.update();
  const pngDataUrl = chart.toBase64Image('image/png', 2);

  if (format === 'png') {
    return dataUrlToBlob(pngDataUrl);
  }

  const img = await loadImage(pngDataUrl);
  const offscreen = document.createElement('canvas');
  offscreen.width = img.width;
  offscreen.height = img.height;
  const ctx = offscreen.getContext('2d');
  if (!ctx) return null;

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, offscreen.width, offscreen.height);
  ctx.drawImage(img, 0, 0);
  return dataUrlToBlob(offscreen.toDataURL('image/jpeg', 0.92));
}

async function exportFromElementSnapshot(
  element: HTMLElement,
  format: MsrChartImageFormat
): Promise<Blob | null> {
  const canvas = await html2canvas(element, {
    backgroundColor: format === 'png' ? null : '#ffffff',
    scale: 2,
    logging: false,
    useCORS: true,
  });

  const mime = format === 'png' ? 'image/png' : 'image/jpeg';
  const quality = format === 'jpeg' ? 0.92 : undefined;
  const dataUrl = canvas.toDataURL(mime, quality);
  return dataUrlToBlob(dataUrl);
}

export async function exportMsrChartImage(
  elementId: string,
  format: MsrChartImageFormat
): Promise<{ blob: Blob; fileName: string } | null> {
  const root = document.getElementById(elementId);
  if (!root) return null;

  const canvas = root.querySelector('canvas');
  let blob: Blob | null = null;

  if (canvas instanceof HTMLCanvasElement) {
    blob = await exportFromChartJsCanvas(canvas, format);
  }

  if (!blob) {
    blob = await exportFromElementSnapshot(root, format);
  }

  if (!blob) return null;

  const chartTitle =
    root.querySelector('h3')?.textContent?.trim() ||
    elementId.replace(/^msr-chart-/, '').replace(/-/g, '_');

  return {
    blob,
    fileName: chartFilename(chartTitle, format),
  };
}

export function downloadMsrChartImage(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
